import { Module } from 'vuex';
import BigNumber from 'bignumber.js';
import { State } from '@/store/models';
import { Asset } from '@/store/models/asset';
import { Payment } from '@/store/models/investment';

export interface AssetsArray<T> extends Array<T> {
  totalLength?: number;
}

export default <Module<Asset[], State>> {
  state: [],
  mutations: {},
  actions: {},
  getters: {
    assetsLoadMore: (state): Function => (position: number, filteredAssets?: Asset[]): Asset[] => {
      const stateAssets = filteredAssets || state;
      const assets: AssetsArray<any> = stateAssets.slice(
        0, position > stateAssets.length ? stateAssets.length : position,
      );

      assets.totalLength = stateAssets.length;

      return assets;
    },
    getAssetById: (state): Function =>
      (id: string): Asset | undefined =>
        state.find((asset): boolean => asset.id === id),
    // Get all the assets that the user has invested in (ToDo: improve 'any' typing)
    getInvestedAssets: (state, getters): Asset[] => {
      const assets: { [key: string]: Asset } = {};
      // Not using a map because it could return an array of undefined
      getters.getPaidPayments.forEach((payment: Payment): any => {
        // Could be that the asset is not retrieved yet && check if we have already inserted it
        if (payment.asset.id && !assets[payment.asset.id]) {
          assets[payment.asset.id] = payment.asset as Asset;
        }
      });
      return Object.values(assets);
    },
    getInvestedPaidOpenAssets: (state, getters): Asset[] => {
      const assets: { [key: string]: Asset } = {};
      // Not using a map because it could return an array of undefined
      getters.getPaidOpenPayments.forEach((payment: Payment): any => {
        // Could be that the asset is not retrieved yet && check if we have already inserted it
        if (payment.asset.id && !assets[payment.asset.id]) {
          assets[payment.asset.id] = payment.asset as Asset;
        }
      });
      return Object.values(assets);
    },
    getNotInvestedAssets: (state, getters): Asset[] =>
      state.filter((asset): boolean =>
        !getters.getInvestedAssets.map((asset: Asset): string => asset.id!).includes(asset.id)),
    getAssetByInvestmentId: (state, getters, rootState): Function =>
      (investmentId: string): Asset | undefined =>
        state.find((asset): boolean => {
          const investment = rootState.investments.find(
            (investment): boolean => investment.id === investmentId,
          );

          return !!investment && (investment.asset.id === asset.id);
        }),
    // Avoiding mutations
    getAssetsOrderedByCreatedDatetimeAsc: (state): Asset[] => [...state].sort((a, b): number => a.createdDateTime.seconds - b.createdDateTime.seconds),
    getAssetValuationSharePrice: (state, getters): Function => (assetId: string): number => {
      const assetValuation = getters.getActiveValuationByAsset(assetId);
      if (!assetValuation) {
        const asset: Asset = getters.getAssetById(assetId);
        return asset.sharePrice;
      }
      return new BigNumber(assetValuation.amount).dividedBy(assetValuation.asset!.totalValueShares).toNumber();
    },
    getAssetValuationAvailableAmount: (state, getters): Function => (asset: Asset): number => {
      const assetSharePrice = getters.getAssetValuationSharePrice(asset.id);
      if (!assetSharePrice) {
        return assetSharePrice;
      }
      return new BigNumber(assetSharePrice).times(asset!.sharesAvailable).decimalPlaces(2).toNumber();
    },
  },
};
